"use client";

import React, { useState, useEffect } from 'react';
import Link from 'next/link';
import Image from 'next/image';
import Head from 'next/head';
import { useRouter } from 'next/navigation';
import Sidebar from '../components/layout/Sidebar';
import MobileNavigation from '../components/layout/MobileNavigation';
import MobileHeader from '../components/layout/MobileHeader';
import { FaSearch, FaFilter, FaClock, FaCheckCircle, FaTimes, FaMotorcycle } from 'react-icons/fa';

export default function OrdersPage() {
  const router = useRouter();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [activeFilter, setActiveFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  
  // Dummy order data
  const [orders, setOrders] = useState([
    {
      id: 'OM1234',
      restaurant: {
        name: 'Burger King',
        image: '🍔',
        address: 'Shop #5, Food Street, Block 13-D, Gulshan-e-Iqbal, Karachi'
      },
      items: [
        { name: 'Whopper', quantity: 2, price: 850 },
        { name: 'French Fries', quantity: 1, price: 350 },
        { name: 'Coca Cola', quantity: 2, price: 150 }
      ],
      total: 2350,
      status: 'delivered',
      date: '2024-03-15T18:30:00',
      deliveryTime: '35 min',
      paymentMethod: 'card'
    },
    {
      id: 'OM2345',
      restaurant: {
        name: 'Pizza Hut',
        image: '🍕',
        address: '27 Main Boulevard, Gulberg III, Lahore'
      },
      items: [
        { name: 'Super Supreme', quantity: 1, price: 1400 },
        { name: 'Garlic Bread', quantity: 1, price: 450 }
      ],
      total: 1850,
      status: 'active',
      date: '2024-03-16T14:20:00',
      deliveryTime: '25 min',
      paymentMethod: 'googlepay'
    },
    {
      id: 'OM3456',
      restaurant: {
        name: 'KFC',
        image: '🍗',
        address: 'Shop #12, F-7 Markaz, Jinnah Super, Islamabad'
      },
      items: [
        { name: 'Zinger Burger', quantity: 1, price: 650 },
        { name: 'Hot Wings (6pcs)', quantity: 1, price: 500 },
        { name: 'Pepsi', quantity: 1, price: 150 }
      ],
      total: 1300,
      status: 'cancelled',
      date: '2024-03-14T20:15:00',
      cancellationReason: 'Restaurant was too busy',
      paymentMethod: 'cod'
    },
    {
      id: 'OM4567',
      restaurant: {
        name: 'Burger King',
        image: '🍔',
        address: 'Shop #5, Food Street, Block 13-D, Gulshan-e-Iqbal, Karachi'
      },
      items: [
        { name: 'Double Whopper', quantity: 1, price: 1050 },
        { name: 'Onion Rings', quantity: 1, price: 370 }
      ],
      total: 1420,
      status: 'delivered',
      date: '2024-03-10T13:45:00',
      deliveryTime: '40 min',
      paymentMethod: 'easypaisa'
    },
    {
      id: 'OM5678',
      restaurant: {
        name: 'Pizza Hut',
        image: '🍕',
        address: '27 Main Boulevard, Gulberg III, Lahore'
      },
      items: [
        { name: 'Cheese Lovers', quantity: 1, price: 1200 },
        { name: 'Buffalo Wings', quantity: 1, price: 650 }
      ],
      total: 1850,
      status: 'preparing',
      date: '2024-03-16T15:10:00',
      estimatedDelivery: '40-50 min',
      paymentMethod: 'jazzcash'
    }
  ]);
  
  // Filter orders based on active filter and search query
  const filteredOrders = orders.filter(order => {
    const matchesFilter = 
      activeFilter === 'all' || 
      (activeFilter === 'active' && ['preparing', 'active'].includes(order.status)) ||
      (activeFilter === 'delivered' && order.status === 'delivered') ||
      (activeFilter === 'cancelled' && order.status === 'cancelled');
    
    const matchesSearch = 
      order.id.toLowerCase().includes(searchQuery.toLowerCase()) ||
      order.restaurant.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      order.items.some(item => item.name.toLowerCase().includes(searchQuery.toLowerCase()));
    
    return matchesFilter && matchesSearch;
  });
  
  // Sort orders by date (newest first)
  const sortedOrders = [...filteredOrders].sort((a, b) => 
    new Date(b.date) - new Date(a.date)
  );
  
  // Function to format date
  const formatDate = (dateString) => {
    const options = { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    };
    return new Date(dateString).toLocaleDateString('en-US', options);
  };
  
  // Function to get status badge styling
  const getStatusBadge = (status) => {
    switch(status) {
      case 'active':
        return {
          color: 'bg-blue-100 text-blue-700',
          icon: <FaMotorcycle className="mr-1" />,
          label: 'On the way'
        };
      case 'preparing':
        return {
          color: 'bg-yellow-100 text-yellow-700',
          icon: <FaClock className="mr-1" />,
          label: 'Preparing'
        };
      case 'delivered':
        return {
          color: 'bg-green-100 text-green-700',
          icon: <FaCheckCircle className="mr-1" />,
          label: 'Delivered'
        };
      case 'cancelled':
        return {
          color: 'bg-red-100 text-red-700',
          icon: <FaTimes className="mr-1" />,
          label: 'Cancelled'
        };
      default:
        return {
          color: 'bg-gray-100 text-gray-700',
          icon: null,
          label: status.charAt(0).toUpperCase() + status.slice(1)
        };
    }
  };
  
  // Function to handle filter change
  const handleFilterChange = (filter) => {
    setActiveFilter(filter);
  };
  
  // Function to render order item
  const renderOrderItem = (order) => {
    const statusBadge = getStatusBadge(order.status);
    
    return (
      <div 
        key={order.id} 
        className="bg-white rounded-lg shadow-sm hover:shadow-md transition-shadow duration-300 mb-4 overflow-hidden"
      >
        <div className="p-4 border-b border-gray-100">
          <div className="flex justify-between items-center mb-2">
            <Link href={`/orders/${order.id}`} className="font-bold text-primary hover:underline">
              Order #{order.id}
            </Link>
            <div className={`flex items-center px-3 py-1 rounded-full text-sm ${statusBadge.color}`}>
              {statusBadge.icon}
              <span>{statusBadge.label}</span>
            </div>
          </div>
          <p className="text-sm text-gray-500">{formatDate(order.date)}</p>
        </div>
        
        <div className="p-4 border-b border-gray-100 flex items-center">
          <div className="bg-primary text-white w-12 h-12 rounded-full flex items-center justify-center text-xl mr-3 flex-shrink-0">
            {order.restaurant.image}
          </div>
          <div>
            <h3 className="font-medium">{order.restaurant.name}</h3>
            <p className="text-sm text-gray-500 truncate max-w-xs">{order.restaurant.address}</p>
          </div>
        </div>
        
        <div className="p-4 border-b border-gray-100">
          <p className="font-medium mb-2">Order Items:</p>
          <div className="space-y-1">
            {order.items.map((item, idx) => (
              <div key={idx} className="flex justify-between text-sm">
                <span>{item.quantity}× {item.name}</span>
                <span>Rs. {item.price * item.quantity}</span>
              </div>
            ))}
          </div>
        </div>
        
        <div className="p-4 flex justify-between items-center">
          <div>
            <p className="text-sm text-gray-500">
              Payment Method: <span className="font-medium">
                {order.paymentMethod === 'cod' && 'Cash On Delivery'}
                {order.paymentMethod === 'card' && 'Credit/Debit Card'}
                {order.paymentMethod === 'easypaisa' && 'Easypaisa'}
                {order.paymentMethod === 'jazzcash' && 'JazzCash'}
                {order.paymentMethod === 'googlepay' && 'Google Pay'}
              </span>
            </p>
            {order.status === 'active' && order.deliveryTime && (
              <p className="text-sm text-gray-500">
                Estimated Delivery: <span className="font-medium">{order.deliveryTime}</span>
              </p>
            )}
            {order.status === 'preparing' && order.estimatedDelivery && (
              <p className="text-sm text-gray-500">
                Estimated Delivery: <span className="font-medium">{order.estimatedDelivery}</span>
              </p>
            )}
            {order.status === 'delivered' && order.deliveryTime && (
              <p className="text-sm text-gray-500">
                Delivered in: <span className="font-medium">{order.deliveryTime}</span>
              </p>
            )}
            {order.status === 'cancelled' && order.cancellationReason && (
              <p className="text-sm text-gray-500">
                Reason: <span className="font-medium">{order.cancellationReason}</span>
              </p>
            )}
          </div>
          <div className="text-right">
            <p className="text-sm text-gray-500">Total Amount</p>
            <p className="font-bold text-lg">Rs. {order.total}</p>
          </div>
        </div>
        
        <div className="p-4 bg-gray-50">
          <Link 
            href={`/orders/${order.id}`}
            className="text-primary font-medium hover:underline flex justify-center"
          >
            View Order Details
          </Link>
        </div>
      </div>
    );
  };
  
  // Update document title and meta tags manually
  useEffect(() => {
    // Set document title
    document.title = "My Orders | Order History | OpenMenu.pk";
    
    // Set meta description
    let metaDescription = document.querySelector('meta[name="description"]');
    if (!metaDescription) {
      metaDescription = document.createElement('meta');
      metaDescription.name = "description";
      document.head.appendChild(metaDescription);
    }
    metaDescription.content = "View your order history, track current orders, and reorder your favorite meals from restaurants across Pakistan.";
  }, []);
  
  return (
    <>
      <Head>
        <title>My Orders | Order History | OpenMenu.pk</title>
        <meta name="description" content="View your order history, track current orders, and reorder your favorite meals from restaurants across Pakistan." />
      </Head>
      
      <div className="flex min-h-screen bg-gray-50">
        {/* Sidebar */}
        <Sidebar mobileOpen={mobileOpen} setMobileOpen={setMobileOpen} />
        
        {/* Main Content */}
        <div className="flex-1 min-h-screen flex flex-col">
          {/* Mobile Header - Fixed for mobile */}
          <MobileHeader setMobileOpen={setMobileOpen} />
          
          {/* Account Navigation */}
          <div className="bg-white shadow-sm border-b">
            <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
              <div className="flex justify-between h-16">
                <div className="flex">
                  <div className="flex-shrink-0 flex items-center">
                    <h1 className="text-xl font-bold text-gray-900">My Account</h1>
                  </div>
                  <nav className="hidden sm:ml-6 sm:flex sm:space-x-8">
                    <Link 
                      href="/account/profile"
                      className="inline-flex items-center px-1 pt-1 border-b-2 border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700 text-sm font-medium"
                    >
                      Profile
                    </Link>
                    <Link 
                      href="/account/addresses"
                      className="inline-flex items-center px-1 pt-1 border-b-2 border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700 text-sm font-medium"
                    >
                      Addresses
                    </Link>
                    <Link 
                      href="/orders"
                      className="inline-flex items-center px-1 pt-1 border-b-2 border-primary text-gray-900 text-sm font-medium"
                    >
                      Orders
                    </Link>
                  </nav>
                </div>
              </div>
            </div>
          </div>
          
          {/* Orders list content */}
          <div className="flex-1 py-6 px-4 sm:px-6 lg:px-8">
            <div className="max-w-4xl mx-auto">
              <h1 className="text-2xl md:text-3xl font-bold mb-6">My Orders</h1>
              
              {/* Search and filter bar */}
              <div className="bg-white rounded-lg shadow-sm p-4 mb-6">
                <div className="flex flex-col md:flex-row gap-4">
                  <div className="relative flex-1">
                    <input
                      type="text"
                      placeholder="Search orders by ID, restaurant, or items..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="w-full p-2 pl-10 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary focus:outline-none"
                    />
                    <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                  </div>
                  
                  <div className="flex items-center">
                    <FaFilter className="mr-2 text-gray-500" />
                    <div className="flex overflow-x-auto hide-scrollbar space-x-2">
                      <button 
                        onClick={() => handleFilterChange('all')}
                        className={`px-3 py-1.5 rounded-full text-sm whitespace-nowrap ${activeFilter === 'all' 
                          ? 'bg-primary text-white' 
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
                      >
                        All Orders
                      </button>
                      <button 
                        onClick={() => handleFilterChange('active')}
                        className={`px-3 py-1.5 rounded-full text-sm whitespace-nowrap ${activeFilter === 'active' 
                          ? 'bg-blue-500 text-white' 
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
                      >
                        Active Orders
                      </button>
                      <button 
                        onClick={() => handleFilterChange('delivered')}
                        className={`px-3 py-1.5 rounded-full text-sm whitespace-nowrap ${activeFilter === 'delivered' 
                          ? 'bg-green-500 text-white' 
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
                      >
                        Delivered
                      </button>
                      <button 
                        onClick={() => handleFilterChange('cancelled')}
                        className={`px-3 py-1.5 rounded-full text-sm whitespace-nowrap ${activeFilter === 'cancelled' 
                          ? 'bg-red-500 text-white' 
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
                      >
                        Cancelled
                      </button>
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Orders list */}
              <div className="space-y-4 mb-20">
                {sortedOrders.length > 0 ? (
                  sortedOrders.map(order => renderOrderItem(order))
                ) : (
                  <div className="bg-white rounded-lg shadow-sm p-8 text-center">
                    <div className="text-4xl mb-3">📋</div>
                    <h3 className="text-xl font-medium mb-2">No orders found</h3>
                    <p className="text-gray-500 mb-4">
                      {searchQuery ? 
                        `No orders match your search "${searchQuery}".` : 
                        `You don't have any ${activeFilter !== 'all' ? activeFilter : ''} orders yet.`}
                    </p>
                    <Link href="/" className="text-primary font-medium hover:underline">
                      Browse restaurants
                    </Link>
                  </div>
                )}
              </div>
            </div>
          </div>
          
          {/* Mobile Navigation */}
          <MobileNavigation />
        </div>
      </div>
    </>
  );
} 